import React, { createContext, useContext, useReducer, useEffect } from 'react';
import api from '../services/api'; // Import the API service

// Create the context
const UserContext = createContext();

// Initial state
const initialState = {
  user: null,
  token: null,
  isAdmin: false,
};

// Reducer to manage user state
const userReducer = (state, action) => {
  switch (action.type) {
    case 'SET_USER_INFO':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
      };
    case 'SET_IS_ADMIN':
      return {
        ...state,
        isAdmin: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAdmin: false,
      };
    case 'UPDATE_USER_PROFILE':
      return {
        ...state,
        user: {
          ...state.user,
          ...action.payload
        }
      };
    default:
      return state;
  }
};

// Provider component
export const UserProvider = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  // Load user data from localStorage on initial load
  useEffect(() => {
    const userInfoFromStorage = localStorage.getItem('userInfo');
    if (userInfoFromStorage) {
      const parsedUserInfo = JSON.parse(userInfoFromStorage);
      dispatch({
        type: 'SET_USER_INFO',
        payload: {
          user: parsedUserInfo,
          token: parsedUserInfo.token,
        },
      });

      // Check if user is admin
      const checkAdminStatus = async () => {
        try {
          const token = parsedUserInfo.token;
          if (token) {
            const response = await api.get('/api/admin/check-admin', {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 2000
            });
            dispatch({ type: 'SET_IS_ADMIN', payload: response.data.isAdmin });
          }
        } catch (error) {
          console.error('Erro ao verificar status de administrador:', error);
          dispatch({ type: 'SET_IS_ADMIN', payload: false });
        }
      };

      checkAdminStatus();
    }
  }, []);



  // Function to handle user login
  const login = (userInfo) => {
    // Store user info in localStorage
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
    
    // Update state
    dispatch({
      type: 'SET_USER_INFO',
      payload: {
        user: userInfo,
        token: userInfo.token,
      },
    });

    // Check admin status
    const checkAdminStatus = async () => {
      try {
        const response = await api.get('/api/admin/check-admin', {
          headers: { Authorization: `Bearer ${userInfo.token}` },
          timeout: 2000
        });
        dispatch({ type: 'SET_IS_ADMIN', payload: response.data.isAdmin });
      } catch (error) {
        console.error('Erro ao verificar status de administrador:', error);
        dispatch({ type: 'SET_IS_ADMIN', payload: false });
      }
    };

    checkAdminStatus();
  };

  // Function to handle user logout
  const logout = () => {
    // Remove user info from localStorage
    localStorage.removeItem('userInfo');

    // Update state
    dispatch({ type: 'LOGOUT' });
  };

  // Function to update user profile
  const updateUserProfile = (updatedData) => {
    const updatedUser = { ...state.user, ...updatedData };
    localStorage.setItem('userInfo', JSON.stringify(updatedUser));
    dispatch({ type: 'UPDATE_USER_PROFILE', payload: updatedData });
  };


  // Value provided to consumers
  const value = {
    user: state.user,
    token: state.token,
    isAdmin: state.isAdmin,
    dispatch,
    login,
    logout,
    updateUserProfile,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Custom hook to use the UserContext
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};